import { useEffect, useMemo, useState } from 'react'
import BaseModal from '../../../components/BaseModal'
import AppCombobox from '../../../components/AppCombobox'

export default function AssignAgentModal({
  isOpen,
  onClose,
  ticket,
  agents = [],
  onAssign,
  isSaving = false,
}) {
  const [selectedAgentId, setSelectedAgentId] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen && ticket) {
      setSelectedAgentId(ticket.assignedAgentId ? String(ticket.assignedAgentId) : '')
      setError('')
    }
  }, [isOpen, ticket])

  const agentOptions = useMemo(() => {
    return (agents || []).map((agent) => {
      const roleBadge = agent.role === 'DEVELOPER' ? '[Developer]' : '[Support Agent]'
      return {
        value: String(agent.user_id || agent.id),
        label: `${agent.email} ${roleBadge}`,
      }
    })
  }, [agents])

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!selectedAgentId) {
      setError('Please select an agent to assign.')
      return
    }
    try {
      setError('')
      await onAssign?.(ticket?.id, Number(selectedAgentId))
      onClose?.()
    } catch (err) {
      setError(err?.message || 'Failed to assign ticket to agent.')
    }
  }

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isSaving}
        className="rounded-xl border border-outline-variant/30 px-4 py-2 text-sm font-semibold text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSaving || !selectedAgentId}
        className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-on-primary shadow hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2"
      >
        {isSaving && (
          <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
        )}
        Assign Ticket
      </button>
    </div>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Ticket to Agent"
      subtitle={ticket ? `${ticket.ticketNo} — ${ticket.subject}` : 'Select a platform agent'}
      footer={footer}
      widthClassName="max-w-md"
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-xl bg-error-container/20 border border-error/30 p-3 text-xs text-error font-medium">
            {error}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant mb-1.5">
            Select Platform Agent <span className="text-error">*</span>
          </label>
          <AppCombobox
            options={agentOptions}
            value={selectedAgentId}
            onChange={(val) => {
              setSelectedAgentId(val ? String(val) : '')
              setError('')
            }}
            placeholder="Choose an agent or developer..."
            searchPlaceholder="Search agent by email..."
            emptyText="No platform agents found."
            className="w-full"
          />
          <p className="mt-1.5 text-[11px] text-on-surface-variant/70">
            Only active Support Agents and Developers in the platform staff registry can be assigned.
          </p>
        </div>
      </div>
    </BaseModal>
  )
}
